package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.Radius
import org.jetbrains.compose.web.css.px
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.accept
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLInputElement
import org.w3c.files.Blob
import org.w3c.files.get
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.math.roundToInt
import dev.lingvo.shared.ProjectAccentKey
import dev.lingvo.shared.ProjectIconKey
import dev.lingvo.site.components.ProjectIcon
import dev.lingvo.site.components.forgetProjectIcon
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.LineStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.compose.ui.modifiers.border

/**
 * A project's own mark, uploaded from a file.
 *
 * **The control is a `<label>` wrapping a hidden `<input type=file>`, not a button that clicks one.**
 * A file picker can only be opened by a genuine user gesture on the input itself; driving it from a
 * button's `onClick` works until a browser tightens its gesture rules, and it loses the keyboard
 * and assistive-technology behaviour the native control already has for free. The input is hidden
 * with `LVisuallyHiddenStyle` rather than `display: none`, because a display-none input is not
 * focusable and the label would have nothing to hand focus to.
 *
 * The size and type are checked here as well as on the server. Not instead of — the server's check
 * is the one that matters and this one is unauthoritative by construction — but so the reader is
 * told what is wrong before a 64KB upload travels and comes back refused.
 */
@Composable
internal fun ProjectIconUpload(
    slug: String,
    name: String,
    iconKey: ProjectIconKey,
    accentKey: ProjectAccentKey,
    hasCustomIcon: Boolean,
    onChanged: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var busy by remember { mutableStateOf(false) }
    var failure by remember(slug) { mutableStateOf<String?>(null) }

    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        Row(Modifier.fillMaxWidth().gap(Space.MD), verticalAlignment = Alignment.CenterVertically) {
            // The shared component, so this preview and every other surface draw the same thing
            // by the same path. An `<img>` pointed straight at the endpoint cannot work — it is
            // behind the bearer gate and an image element sends no Authorization header.
            ProjectIcon(
                slug = slug,
                name = name,
                iconKey = iconKey,
                accentKey = accentKey,
                hasCustomIcon = hasCustomIcon,
                modifier = Modifier.width(ICON_PREVIEW_PX.px).height(ICON_PREVIEW_PX.px),
            )
            Column(Modifier.gap(Space.XS)) {
                // **Styled as a control, because it is one.** This was a bare `Label` with an
                // empty modifier: plain body text with no affordance, no focus ring, and a hit
                // target under the 40px floor. The input inside it is visually hidden, so the
                // label is the only thing a reader can see or focus — `:focus-within` is what
                // moves the ring from the invisible input onto the visible control.
                Label(attrs = ProjectIconUploadLabelStyle.toModifier().toAttrs()) {
                    // The native control, kept reachable rather than replaced.
                    Input(
                        type = InputType.File,
                        attrs = LVisuallyHiddenStyle.toModifier().toAttrs {
                            accept(ACCEPTED_TYPES)
                            attr("data-project-icon-input", "true")
                            onChange { event ->
                                val input = event.target as? HTMLInputElement
                                val file = input?.files?.get(0) ?: return@onChange
                                // Cleared so choosing the same file twice fires `change` again —
                                // otherwise a reader who fixes the file and re-picks it sees
                                // nothing happen.
                                input.value = ""
                                when {
                                    file.size.toLong() > MAX_ICON_BYTES -> failure = tr(
                                        "app.projects.slug.settings.project_icon_upload.too_large",
                                        "That file is larger than 64KB. Export it at 128×128 and try again.",
                                    )
                                    else -> {
                                        failure = null
                                        busy = true
                                        scope.launch {
                                            try {
                                                // A phone photo routinely arrives at several
                                                // thousand pixels a side and comfortably under
                                                // 64KB once compressed — the byte cap alone does
                                                // not stop it. Downscaling here means the common
                                                // case never has to round-trip to the server to
                                                // find out its canvas is too large; a failure to
                                                // downscale (an old browser, a corrupt file) falls
                                                // through to the original bytes and the server's
                                                // own check.
                                                val upload = try {
                                                    downscaleProjectIcon(file)
                                                } catch (cancelled: CancellationException) {
                                                    throw cancelled
                                                } catch (_: Throwable) {
                                                    file
                                                }
                                                ApiClient.uploadProjectIcon(slug, upload)
                                                // The cache holds an object URL the browser will
                                                // never re-read. Dropping it is what makes the
                                                // replacement visible.
                                                forgetProjectIcon(slug)
                                                onChanged()
                                            } catch (error: ApiException) {
                                                failure = error.message
                                            } finally {
                                                busy = false
                                            }
                                        }
                                    }
                                }
                            }
                        },
                    )
                    // `Text` inside the label rather than a `LButton`: the label is the control, and
                    // a button nested in it would be a second focusable thing doing the same job.
                    Text(
                        if (hasCustomIcon) {
                            t("app.projects.slug.settings.project_icon_upload.replace", "Replace icon")
                        } else {
                            t("app.projects.slug.settings.project_icon_upload.upload", "Upload an icon")
                        },
                    )
                }
                LMuted(
                    t(
                        "app.projects.slug.settings.project_icon_upload.hint",
                        "PNG, JPEG or WebP, up to 64KB. Square works best. SVG is not accepted " +
                            "because it can carry scripts.",
                    ),
                )
            }
            if (hasCustomIcon) {
                LButton(
                    label = t("app.projects.slug.settings.project_icon_upload.remove", "Remove"),
                    onClick = {
                        busy = true
                        scope.launch {
                            try {
                                ApiClient.deleteProjectIcon(slug)
                                forgetProjectIcon(slug)
                                onChanged()
                            } catch (error: ApiException) {
                                failure = error.message
                            } finally {
                                busy = false
                            }
                        }
                    },
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    enabled = !busy,
                    accessibleLabel = t(
                        "app.projects.slug.settings.project_icon_upload.remove_custom_icon",
                        "Remove the custom icon and go back to the preset",
                    ),
                )
            }
        }
        failure?.let { message ->
            LAlert(
                t("app.projects.slug.settings.project_icon_upload.couldnt_save", "Couldn't save that icon"),
                message,
                variant = LAlertVariant.WARNING,
            )
        }
    }
}

/**
 * The chosen file, resized to at most [DOWNSCALE_MAX_PX] on its longer side, or the original when
 * it is already that small or smaller.
 *
 * `createImageBitmap` and `HTMLCanvasElement.toBlob` are both reached through `asDynamic()` rather
 * than typed Kotlin/JS bindings — `toBlob` in particular is not declared on the stdlib's
 * `HTMLCanvasElement`, and a dynamic call here is confined to this one function rather than
 * spreading `js()` calls through the composable above it. Any failure — an old browser without
 * `createImageBitmap`, a file that is not actually decodable as an image — throws, and the caller
 * falls back to uploading the original file and letting the server's own check answer.
 */
private suspend fun downscaleProjectIcon(file: Blob): Blob {
    val bitmap = (window.asDynamic().createImageBitmap(file).unsafeCast<kotlin.js.Promise<dynamic>>()).await()
    val width = bitmap.width as Int
    val height = bitmap.height as Int
    if (width <= DOWNSCALE_MAX_PX && height <= DOWNSCALE_MAX_PX) {
        bitmap.close()
        return file
    }
    val scale = DOWNSCALE_MAX_PX.toDouble() / maxOf(width, height)
    val targetWidth = (width * scale).roundToInt().coerceAtLeast(1)
    val targetHeight = (height * scale).roundToInt().coerceAtLeast(1)

    val canvas = document.createElement("canvas") as HTMLCanvasElement
    canvas.width = targetWidth
    canvas.height = targetHeight
    val context = canvas.getContext("2d") ?: run {
        bitmap.close()
        return file
    }
    context.asDynamic().drawImage(bitmap, 0, 0, targetWidth, targetHeight)
    bitmap.close()
    return canvasToPngBlob(canvas)
}

private suspend fun canvasToPngBlob(canvas: HTMLCanvasElement): Blob =
    suspendCancellableCoroutine { continuation ->
        canvas.asDynamic().toBlob(
            { blob: Blob? ->
                if (blob != null) {
                    continuation.resumeWith(Result.success(blob))
                } else {
                    continuation.resumeWith(Result.failure(IllegalStateException("canvas.toBlob returned null")))
                }
            },
            "image/png",
        )
    }

/**
 * The chosen file is downscaled only above this — a 256px mark is already generous for every place
 * `ProjectIcon` renders one, and 256×256 stays comfortably under the server's 512px refusal.
 */
private const val DOWNSCALE_MAX_PX = 256

/** Matches the server's allowlist. A hint to the file picker, never a check — the server decides. */
private const val ACCEPTED_TYPES = "image/png,image/jpeg,image/webp"

/** The server's own cap, repeated so the refusal arrives before the upload does. */
private const val MAX_ICON_BYTES = 64L * 1024L

private const val ICON_PREVIEW_PX = 48

/**
 * The file-picker label, styled as the secondary control it stands in for.
 *
 * The input inside is visually hidden, so the label is the only thing on screen and the only thing
 * a reader can click. As a bare `<label>` it rendered as plain body text: nothing said it was
 * pressable, nothing showed when it took focus, and its hit area was whatever the text happened to
 * measure — below the 40px floor `scripts/check-control-geometry.mjs` enforces on every other
 * control in the product.
 *
 * `:focus-within` rather than `:focus`. Focus lands on the hidden input, not on the label, so a
 * ring on the label itself would never appear; `:focus-within` is what moves it to the element the
 * reader can actually see.
 */
val ProjectIconUploadLabelStyle = CssStyle {
    base {
        Modifier
            .height(CONTROL_HEIGHT_PX.px)
            .padding(leftRight = Space.LG)
            .borderRadius(Radius.Control)
            // `borderStrong`, the boundary `LButtonVariant.SECONDARY` draws. `border` is the
            // divider token — 1.32:1 on paper — and `docs/DESIGN.md` marks it illegal as a control
            // boundary for that reason: it is the only pressable thing on this row, and it was
            // drawn with an edge a low-vision reader cannot resolve against the surface behind it.
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .backgroundColor(colorMode.toSitePalette().surface)
            .color(colorMode.toSitePalette().textPrimary)
            .fontSize(TypeScale.BodySmall)
            .fontWeight(600)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("box-sizing", "border-box")
                property("white-space", "nowrap")
            }
    }
    hover { Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken) }
    cssRule(":focus-within") {
        Modifier.styleModifier {
            property("outline", "2px solid ${colorMode.toSitePalette().accent}")
            property("outline-offset", "2px")
        }
    }
}

/** `LButtonSize.MD`, so this sits in a row with real buttons without looking like a near-miss. */
private const val CONTROL_HEIGHT_PX = 40
